import { type QueryObserverResult, type RefetchOptions } from '@tanstack/react-query';
import { TradingToast, useToast, useTradingTheme } from '@trading-ui/core';
import type { CellComponent, Options, TradingDatagridV3Interface } from '@trading-ui/datagrid-v3';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useConsumerTableStore from '@/views/consumer_view/store/consumer-table.store';

import { updateConsumptions } from '@/api/services/consumer/consumer.service';
import { parseBackendReason } from '@/utils/error.utils';

import type {
  Consumption,
  ConsumptionChartResponse,
  ConsumptionTableResponse,
  ProcessedBackendError,
} from '@/interfaces';

import { useDebounceCallback } from 'usehooks-ts';
import { getColumns } from '../constants/columns';

type Params = {
  mutateChart: (options?: RefetchOptions) => Promise<QueryObserverResult<ConsumptionChartResponse, Error>>;
  mutateTable: (options?: RefetchOptions) => Promise<QueryObserverResult<ConsumptionTableResponse, Error>>;
  height: number;
};

// Function to transform the parsed text on Table to an array of numbers
const transformPastedText = (pastedText: string): number[][] => {
  const transformedArray = pastedText
    .trim() // Removes unnecessary spaces at the beginning and end
    .split('\n') // Splits by rows (new line)
    .map(
      (row: string) =>
        row
          .split(/\s+/) // Splits by spaces or tabs
          .filter((val: string) => val !== '') // Removes empty values
          .map(Number) // Converts each value to a number
    );

  return transformedArray;
};

export const useConsumptionTable = ({ mutateChart, mutateTable, height }: Params) => {
  const theme = useTradingTheme();
  const { addToast } = useToast();
  const { i18n, t } = useTranslation();

  const tableInstance = useConsumerTableStore((state) => state.tableInstance);
  const saveTableInstance = useConsumerTableStore((state) => state.saveTableInstance);

  const [isUpdating, setIsUpdating] = useState(false);

  // Regenerate columns reactively when language changes
  const columns = useMemo(
    () => getColumns(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  const handleUpdateConsumptions = useCallback(
    async (
      updates: Array<{
        id: number;
        nomination: number | null;
        plantId: number;
        date: Date;
      }>
    ) => {
      if (updates.length === 0) return;

      setIsUpdating(true);

      try {
        // Update Consumptions
        await updateConsumptions(updates);

        // Refresh data after all updates are complete
        await mutateChart();
        await mutateTable();

        addToast(
          <TradingToast
            type="success"
            title={t('services.updateConsumptions.sucessTitle')}
            message={t('services.updateConsumptions.successMessage', { count: updates.length })}
          />
        );
      } catch (error) {
        addToast(
          <TradingToast
            type="error"
            title="Error"
            message={parseBackendReason(error as Error | ProcessedBackendError)}
          />
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [addToast, mutateChart, mutateTable, t]
  );

  const getTableInstance = useCallback(
    (table: TradingDatagridV3Interface) => {
      if (table) {
        // Store original values when editing starts
        const originalValues = new Map<number, number | null>();

        table.on('cellEditing', (cell: CellComponent) => {
          const column = cell.getColumn();
          const columnField = column.getField();

          if (columnField === 'nomination') {
            const row = cell.getRow();
            const rowData = row.getData() as Consumption;
            // Store the original value before editing
            originalValues.set(rowData.id, rowData.nomination);
          }
        });

        table.on('cellEdited', (cell: CellComponent) => {
          const column = cell.getColumn();
          const columnField = column.getField();

          if (columnField !== 'nomination') {
            return;
          }

          const row = cell.getRow();
          const rowData = row.getData() as Consumption;
          let newValue = cell.getValue() as number | string | null;
          const originalValue = originalValues.get(rowData.id);

          // Convert string to number if needed
          if (typeof newValue === 'string') {
            const parsed = parseFloat(newValue);
            if (Number.isNaN(parsed)) {
              cell.restoreOldValue();
              return;
            }
            newValue = parsed;
          }

          // Validate that we have a valid number (including 0)
          if (typeof newValue !== 'number' || Number.isNaN(newValue)) {
            cell.restoreOldValue();
            return;
          }

          // Only update if the value has actually changed from the original
          if (newValue !== originalValue) {
            handleUpdateConsumptions([
              {
                id: rowData.id,
                nomination: newValue,
                plantId: rowData.plant.id,
                date: rowData.date instanceof Date ? rowData.date : new Date(rowData.date),
              },
            ]);
          }

          // Clean up the stored original value
          originalValues.delete(rowData.id);
        });

        saveTableInstance(table);
      }
    },
    [saveTableInstance, handleUpdateConsumptions]
  );

  const options: Options = useMemo(
    () => ({
      layout: 'fitColumns',
      selectableRange: 1,
      height,
      selectableRangeColumns: true,
      selectableRangeRows: false,
      selectableRangeClearCells: false, // Disable range clearing, we'll handle it manually
      editTriggerEvent: 'dblclick',
      clipboard: true,
      clipboardCopyStyled: false,
      clipboardCopyConfig: {
        rowHeaders: false,
        columnHeaders: false,
      },
      clipboardCopyRowRange: 'range',
      columnDefaults: {
        hozAlign: 'center',
        vertAlign: 'middle',
        headerHozAlign: 'center',
        headerVertAlign: 'middle',
      },
      clipboardPasteParser: (clipboard: string) => {
        // Get the table instance directly from the store to ensure we have the latest value
        const currentTableInstance = useConsumerTableStore.getState().tableInstance;
        if (!currentTableInstance) return [];

        const ranges = currentTableInstance.getRanges();
        if (!ranges || ranges.length === 0) return [];

        const range = ranges[0];
        const rows = range.getRows();
        const columnsInRange = range.getColumns();

        if (!rows || rows.length === 0 || !columnsInRange || columnsInRange.length === 0) return [];

        // Get the column field from the first column in the range
        const firstColumn = columnsInRange[0];
        const columnField = firstColumn.getField();

        // Only allow paste in the 'nomination' column (the only editable column)
        if (columnField !== 'nomination') {
          return [];
        }

        // Check if the range contains only one column
        if (columnsInRange.length > 1) {
          // If multiple columns selected, only allow paste if all columns are the same
          const allSameColumn = columnsInRange.every((column) => column.getField() === columnField);
          if (!allSameColumn) return [];
        }

        // Transform the pasted text to an array of values
        const transformedRows = transformPastedText(clipboard);
        const pastedRowsCount = transformedRows.length;
        const selectedRowsLength = rows.length;

        // If only one cell is selected but multiple values are pasted,
        // expand to include all necessary rows from the table
        if (selectedRowsLength === 1 && pastedRowsCount > 1) {
          // Get the first selected row
          const firstRow = rows[0];
          const firstRowIndex = firstRow.getPosition();

          // Check if getPosition returned a valid number
          if (typeof firstRowIndex !== 'number') {
            return [];
          }

          // Get all rows from the table starting from the selected row
          const allRows = currentTableInstance.getRows();

          // Ensure we start from the correct index (getPosition might be 0-based or 1-based)
          // We'll use the actual row object to find its index in the allRows array
          const actualIndex = allRows.findIndex((r) => r === firstRow);
          const startIndex = actualIndex !== -1 ? actualIndex : firstRowIndex;

          const rowsFromPosition = allRows.slice(startIndex, startIndex + pastedRowsCount);

          // Only use rows that exist (don't exceed table bounds)
          if (rowsFromPosition.length < pastedRowsCount) {
            // Not enough rows available, return empty to prevent partial paste
            return [];
          }

          // Prepare all updates to send to the backend
          const updatesToSend = rowsFromPosition.map((row, rowIndex) => {
            const pastedValue = transformedRows[rowIndex];
            const valueToPaste = Array.isArray(pastedValue) ? pastedValue[0] : pastedValue;
            const rowData = row.getData() as Consumption;

            return {
              id: rowData.id,
              nomination: valueToPaste,
              plantId: rowData.plant.id,
              date: rowData.date,
            };
          });

          // Update all rows manually using Tabulator API (including the first one)
          // Use requestAnimationFrame to ensure updates happen after Tabulator processes the paste
          requestAnimationFrame(() => {
            // Update UI first for immediate feedback
            rowsFromPosition.forEach((row, rowIndex) => {
              const pastedValue = transformedRows[rowIndex];
              const valueToPaste = Array.isArray(pastedValue) ? pastedValue[0] : pastedValue;
              const rowData = row.getData() as Consumption;

              row.update({
                ...rowData,
                [columnField]: valueToPaste,
              });
            });

            // Then update all consumptions in the backend at once
            handleUpdateConsumptions(updatesToSend);
          });

          // Return empty array to prevent Tabulator's default paste behavior
          // We've already updated all rows manually above
          return [];
        }

        // If multiple rows selected, they must match the pasted count
        if (selectedRowsLength !== pastedRowsCount) {
          return [];
        }

        // Build the result array: each row should only update the selected column field
        const result = rows.map((row, rowIndex) => {
          const rowData = row.getData() as Consumption;
          const pastedValue = transformedRows[rowIndex];

          // If pasted value is an array, take the first element (single column paste)
          const valueToPaste = Array.isArray(pastedValue) ? pastedValue[0] : pastedValue;

          const updatedRow: Consumption = {
            ...rowData,
            [columnField]: valueToPaste,
          } as Consumption;

          return updatedRow;
        });

        return result;
      },
      clipboardPasteAction: 'range',
      cellEditing: (cell: CellComponent) => {
        // Prevent editing non-editable columns (only 'nomination' is editable)
        const column = cell.getColumn();
        const columnField = column.getField();

        if (columnField !== 'nomination') {
          // Cancel editing if trying to edit a non-editable column
          cell.cancelEdit();
        }
      },
      cellEdited: (cell: CellComponent) => {
        // Prevent editing non-editable columns (only 'nomination' is editable)
        // This is a fallback in case cellEditing didn't catch it
        const column = cell.getColumn();
        const columnField = column.getField();

        if (columnField !== 'nomination') {
          // Restore the original value if trying to edit a non-editable column
          cell.restoreOldValue();
        }
      },
    }),
    [handleUpdateConsumptions, height]
  );

  // Update Tabulator columns when columns change and instance exists
  useEffect(() => {
    if (tableInstance && columns.length > 0) {
      // Use setTimeout to ensure Tabulator is ready
      const timeoutId = setTimeout(() => {
        tableInstance.setColumns(columns);
        // Force redraw to ensure column titles are updated
        tableInstance.redraw(true);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    return undefined;
  }, [tableInstance, columns]);

  // Resize handlers
  const handleSetHeight = useCallback(
    (h: number) => {
      if (tableInstance) {
        tableInstance.setHeight(h);
      }
    },
    [tableInstance]
  );

  const debouncedSetHeight = useDebounceCallback(handleSetHeight, 500);

  useEffect(() => {
    if (tableInstance && height > 0) {
      debouncedSetHeight(height);
    }
  }, [height, tableInstance, debouncedSetHeight]);

  return {
    columns,
    isUpdating,
    theme,
    tableInstance,
    options,
    getTableInstance,
  };
};
